import { IClient } from './clients.models';
import { IMotorProduct, MotorProductType } from './motor-product.model';
import { IQueryParams } from './queryparams';
import { IRisk } from './risk-category';
import { IRiskRequest } from './risk-request.model';
import {
  ISourceOfBusiness,
  ISourceOfBusinessRequest,
  SourceOfBusinessType,
  SourcesOfBusinessPlatform,
} from './source-of.business';
import { IBaseModel } from './base.model';
import { Currency } from './currency';

import { IPolicy, RevisionType } from './policy';
import { ICardDetails, PaymentMethod, PaymentStatus } from './payment.model';

import { IIntermediaryResponse } from './intermediary.model';
import { IBankDetail } from './broker.model';
import {
  PaymentRequisitionType,
  RequisitionStatus,
} from './payment-requisition.model';
import { FormControl } from '@angular/forms';
import { IPartner } from './partners.models';
import { IIntermediaryCommission } from './commission';
import { DurationType } from './motor-quotation-model';
import { IRepresentative } from './representative.model';
import { IGeneralPolicy } from './accident-policy.models';
import { IInsurer } from './insurer.model';
import { IPolicyholder } from './policyholder';

export interface IPolicyQueryParams extends IQueryParams {
  statuses: IPolicyStatus[] | null;
  cancellationStatuses: PolicyCancellationStatus[] | null;
  debitNoteStatus: IDebitNoteStatus | null;
  startDate: string | null;
  endDate: string | null;
  insurerId: number | null;
  partnerId: number | null;
  clientId: number | null;
  intermediaryId?: number | null;
  branchId: number | null;
  query: string | null;
  classOfBusinessId: number | null;
  daysRemaining?: number | null;
}

export interface ITransactionReportParams extends IQueryParams {
  intermediaryId: number | null;
  partnerId: number | null;
  insurerId: number | null;
  clientId: number | null;
  cobId: number | null;
  fromDate: string | null;
  toDate: string | null;
  paymentMethods: PaymentMethod[] | null;
  transactionStatus: PaymentStatus[] | null;
  currency: Currency[] | null;
  createdById: number | null;
  sourceTypes?: SourceOfBusinessType[] | null;
  platforms?: SourcesOfBusinessPlatform[] | null;
}

export interface IInsurerQueryParams extends IQueryParams {
  query: string | null;
  partnerId: number | null;
  insurerId: number | null;
  brokerId: number | null;
  sortByPopularity?:boolean;
  classOfBusinessIds?: number[] | null;
}

export interface IRenewalQueryParams extends IQueryParams {
  startDate: Date | null;
  endDate: Date | null;
  insurerId: number | null;
  partnerId: number | null;
  clientId: number | null;
  intermediaryId: number | null;
  query: string | null;
  classOfBusinessId: number | null;
}

export interface IPolicyStatsQueryParams {
  insurerId: number | null;
  partnerId: number | null;
  intermediaryId: number | null;
  clientId: number | null;
  branchId: number | null;
}

export interface IPoliciesReportQueryParams extends IQueryParams {
  intermediaryId: number | null;
  partnerId: number | null;
  insurerId: number | null;
  searQuery: string | null;
  classOfBusinessId: number | null;
  branchId: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  statuses?: IPolicyStatus[] | null;
  createdById?: number | null;
}

export interface IPolicyRenewalReportQueryParams extends IQueryParams {
  policyNumber: string | null;
  insurerId: number | null;
  intermediaryId: number | null;
  clientId: number | null;
  branchId: number | null;
  classOfBusinessId: number | null;
  productId: number | null;
  fromDate: string | null;
  toDate: string | null;
}

export interface IPremiumWorkingQueryParams extends IQueryParams {
  insurerId: number | null;
  createdById: number | null;
  intermediaryId: number | null;
  partnerId: number | null;
  query: string | null;
  fromDate: string | null;
  toDate: string | null;
  branchId: number | null;
}

export interface IPolicyAnalysisReportQueryParams extends IQueryParams {
  intermediaryId: number | null;
  insurerId: number | null;
  branchId: number | null;
  classOfBusinessId: number | null;
  fromDate: string | null;
  toDate: string | null;
  createdById: number | null;
}

export type IPolicyStatus =
  | 'Draft'
  | 'Active'
  | 'Expired'
  | 'Cancelled'
  | 'InClaim'
  | 'Inactive'
  | 'InCancellation';

export type IDebitNoteStatus = 'Paid' | 'NotPaid';

export interface IMotorPolicy extends IPolicy {
  duration: number;
  durationType: DurationType;
  risks: IRisk[];
  product: IMotorProduct;
  policyCancellationHistory: IPolicyCancellationHistory[];
  extensionsPremium: number;
  discountsPremium: number;
  exclusionsPremium?: number;
  warrantiesPremium?: number;
  limitsOfLiabilitiesPremium: number;
  insuredName?: string;
  clauses: string[];
  insurer: IInsurer;
}

export interface IMotorPolicyListing extends IBaseModel{
  policyNumber: string;
  client: string;
  insurer: string;
  intermediary: string;
  product: string;
  premiumLevy: number;
  currency: Currency;
  basicPremium: number;
  grossPremium: number;
  duration: number;
  durationType: DurationType;
  limitsOfLiabilitiesPremium: number;
  extensionsPremium: number;
  discountsPremium: number;
  // MotorPolicyListingLeanResponse uses the plural names, like extensionsPremium
  exclusionsPremium?: number;
  warrantiesPremium?: number;
  status: IPolicyStatus;
  approvedDate: Date;
  startDate: Date;
  endDate: Date;
}

export interface IPolicyInputs {
  intermediaryId?: number | null;
  clientId?: number | null;
  insurerId?: number | null;
  partnerId?: number | null;
  statuses?: IPolicyStatus[];
}

export interface IPolicyEndorsement extends IBaseModel {
  type: PolicyEndorsementType;
  policy: ILeanPolicyResponse;
  createdBy?: IRepresentative;
  snapshot: IMotorPolicy | IGeneralPolicy;
}

export interface ILeanPolicyResponse extends IBaseModel {
  policyNumber: string;
  status: IPolicyStatus;
  approvedDate: string;
  startDate: Date;
  endDate: Date;
  client: IClient;
  premiumLevy: number;
  basicPremium: number;
  grossPremium: number;
}

export type PolicyEndorsementType =
  | 'Initial'
  | 'Renewal'
  | 'Extension'
  | 'Revision'
  | 'NewBusiness'
  | 'ClientUpdate'
  | 'VehicleUpdate'
  | 'RiskUpdate'
  | 'DatesUpdate'
  | 'LimitsOfLiabilityUpdate';

export interface IPolicyExtensionRequest {
  quarters?: number;
  paymentMethod?: PaymentMethod;
  sourceOfBusiness: ISourceOfBusiness;
}

export interface IPolicyExtension {
  product: string;
  underwriter: string;
  grossPremium: number;
  paymentToken: string;
}

export interface IMotorPolicyRenewalRequest {
  duration: number;
  startDate: Date;
  risks: IRiskRequest[];
  paymentMethod: string;
  sourceOfBusiness: Partial<ISourceOfBusiness>;
  mobileMoneyAccountNumber: string | null;
  cardDetails: ICardDetails | null;
}

export type RefundOptionType = 'TimeOnRisk' | 'Custom';
export type PolicyCancellationStatus =
  | 'Initial'
  | 'Approved'
  | 'Rejected'
  | 'Revised'
  | 'AwaitingSettlement'
  | 'Settled';

export const policyCancellationStatuses: PolicyCancellationStatus[] = [
  'Initial',
  'Approved',
  'Rejected',
  'Revised',
  'AwaitingSettlement',
  'Settled',
];

export interface PolicyCancellationRequest {
  cancellationDate: Date;
  optionType: RefundOptionType;
  customAmount: number;
  cancellationReason: string;
}

export interface CancellationRejection {
  rejectionReason: string;
}

export interface NonRefundCancellationRequest {
  cancellationReason: string;
}

export interface MotorPolicyCancellation extends IBaseModel {
  status: PolicyCancellationStatus;
  refundAmount?: number;
  cancellationDate?: string;
  cancelledDate?: string;
  cancellationReason?: string;
  rejectionReason?: string;
  revisionReason?: string;
  timeOnRisk?: number;
  policy?: IMotorPolicy;
}

export interface Requisition extends IBaseModel {
  policy?: IPolicy;
  intermediary?: IIntermediaryResponse;
  partner?: IPartner;
  paymentMethod?: PaymentMethod;
  bankDetail?: IBankDetail;
  mobileNumber?: string;
  paymentRequisitionStatus: RequisitionStatus;
  paymentRequisitionType: PaymentRequisitionType;
  paymentNumber: string;
  amount: number;
  narration?: string;
  payee: string;
  intermediaryCommissions: IIntermediaryCommission[];
}

export interface IPolicyCancellationHistory extends IBaseModel {
  status: PolicyCancellationStatus;
  refundAmount: number;
  customAmount: number;
  cancellationDate: Date;
  cancelledDate: Date;
  reason: string;
  timeOnRisk: number;
  policyId: number;
  policy: IPolicy;
  changes: ICancellationRevisionChanges;
}

export interface ICancellationRevisionChanges {
  prevRefund?: number;
  cancellationDateOnReject?: Date;
  currentRefund?: number;
  cancellationDateOnRevise?: Date;
}

export interface IPolicyCancellationStats {
  pendingCount: number;
  awaitingSettlementCount: number;
  cancelledCount: number;
  totalRefundAmount: number;
}

export interface IIssuePolicyRequest {
  mobileMoneyAccountNumber: string | null;
  cardDetails: ICardDetails | null;
  paymentMethod: PaymentMethod;
  policyHolder?: IPolicyholder;
}

export interface IPolicyMotorRevisionRequest {
  risks: IRiskRequest[];
  paymentMethod: PaymentMethod;
  clauses: string[];
  sourceOfBusiness: ISourceOfBusiness;
  mobileMoneyAccountNumber: string | null;
  cardDetails: ICardDetails | null;
  type: RevisionType;
}

export interface IMotorPolicyExtensionRequest {
  duration: number;
  basicPremium: number | null;
  premiumRate: number | null;
  paymentMethod: PaymentMethod;
  endDate: Date;
  sourceOfBusiness: ISourceOfBusinessRequest;
  mobileMoneyAccountNumber: string | null;
  cardDetails: ICardDetails | null;
}

export interface IPolicyReminderRequest {
  policyId: number;
  daysRemaining: number;
  templateId?: number;
  customMessage?: string;
  emailSubject?: string;
}

export interface IPolicyReminderItem {
  policyId: number;
  daysRemaining: number;
}

export interface IBulkPolicyReminderRequest {
  clientIds?: number[];
  policiesIds?: number[];
  templateId?: number;
  customMessage?: string;
  emailSubject?: string;
  reminderType?: 'PolicyReminder' | 'GeneralReminder';
  scheduledDate?: string;
  daysRemaining?: number;
  insurerId?: number;
}

export interface ReminderResponse {
  success: boolean;
  message?: string;
  reminded?: number;
  failed?: number;
  results?: ReminderResult[];
  requestId?: string;
  timestamp?: string;
}

export interface ReminderResult {
  policyId?: number | string;
  status: 'Success' | 'Failed' | 'Queued' | string;
  message?: string;
}
export interface ICancellationReasonForm {
  cancellationReason: FormControl<string>;
}

export interface PolicyReminderQueryParams extends IQueryParams {
  insurerId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
  policyId?: number;
  status?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
}

export interface PolicyRenewalReminder {
  id: number;
  motorPolicyId?: number;
  policyNumber?: string;
  clientName?: string;
  reminderDays: number;
  reminderType: 'PolicyReminder' | 'GeneralReminder';
  status: 'Active' | 'Sent' | 'Failed';
  createdAt: string;
  sentAt?: string;
  scheduledDate?: string;

  insurerName?: string;

  templateId?: number;
  templateName?: string;

  emailSubject?: string;
  message?: string;
}

export interface IMessageTemplate {
  id: number;
  template: string;
  createdAt: string;
  updatedAt: string;
  message: string;
}

export interface MessageTemplateQueryParams extends IQueryParams {
  query?: string;
  insurerId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
  type?: 'PolicyReminder' | 'GeneralReminder';
}
